import { Platform } from 'react-native';

import { firebaseAnalyticsAdapter } from '@/adapters/analytics';
import { createUserAwareAnalyticsAdapter } from '@/adapters/analytics/createUserAwareAnalyticsAdapter';
import { getCurrentAccountId } from '@/adapters/analytics/currentAccountId';
import { getCurrentUserId } from '@/adapters/analytics/currentUserId';
import { apolloClient } from '@/adapters/graphql/apolloClient';
import { sentryLogger } from '@/adapters/logger/sentryLogger';
import { firebaseMessagingAdapter } from '@/adapters/pushNotification';
import {
  RegisterDeviceDocument,
  UnregisterDeviceDocument,
} from '@/generated/usermobile-bff/graphql';
import { createDeviceRegistrationStore } from '@/stores/createDeviceRegistrationStore';

import type { DeviceRegistrationBffAdapter } from '@/adapters/device/types';

const bffAdapter: DeviceRegistrationBffAdapter = {
  registerDevice: async (token, platform) => {
    const { data } = await apolloClient.mutate({
      mutation: RegisterDeviceDocument,
      variables: { input: { token, platform } },
    });

    if (typeof data?.registerDevice !== 'boolean') {
      throw new Error('registerDevice: no data returned');
    }

    if (!data.registerDevice) {
      throw new Error('registerDevice: mutation returned false');
    }

    return data.registerDevice;
  },
  unregisterDevice: async (token) => {
    const { data } = await apolloClient.mutate({
      mutation: UnregisterDeviceDocument,
      variables: { input: { token } },
    });

    if (typeof data?.unregisterDevice !== 'boolean') {
      throw new Error('unregisterDevice: no data returned');
    }

    if (!data.unregisterDevice) {
      throw new Error('unregisterDevice: mutation returned false');
    }

    return data.unregisterDevice;
  },
};

const useDeviceRegistrationStore = createDeviceRegistrationStore({
  pushNotification: firebaseMessagingAdapter,
  bff: bffAdapter,
  logger: sentryLogger,
  analytics: createUserAwareAnalyticsAdapter(
    firebaseAnalyticsAdapter,
    () => getCurrentUserId(),
    () => getCurrentAccountId(),
  ),
  getPlatform: () => {
    if (Platform.OS === 'ios') {
      return 'IOS';
    }

    if (Platform.OS === 'web') {
      return 'WEB';
    }

    return 'ANDROID';
  },
});

export { useDeviceRegistrationStore };
